import {
  Stack,
  Typography,
  Box,
  Theme,
  Tooltip,
  IconButton,
} from "@mui/material";
import { InfoOutlined, AcUnit } from "@mui/icons-material";
import React from "react";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../../../hooks/useTranslation";
import { Variant } from "../../types";

type Props = {
  label: string;
  variant?: Variant;
  children: React.ReactNode;
  isFrozen?: boolean;
};

export const PriceRowLayout = React.memo(
  ({ label, children, variant = "positive", isFrozen = false }: Props) => {
    const { translate } = useTranslation("pages.orders.detail.profit");

    const getBorderColor = (v: Variant, theme: Theme): string => {
      const colorMap: Record<Variant, string> = {
        positive: alpha(theme.palette.success.main, 0.4),
        negative: alpha(theme.palette.error.main, 0.4),
        neutral: alpha(theme.palette.grey[500], 0.4),
      };

      return colorMap[v];
    };

    const showNeutralInfo = variant === "neutral";

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={(theme) => ({
          px: 2,
          py: 1,
          mb: 1,
          borderRadius: 1,
          border: "2px solid",
          borderColor: getBorderColor(variant, theme),
        })}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {isFrozen && (
            <Tooltip
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {translate("frozenInfo.title")}
                  </Typography>

                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      mt: 0.75,
                      lineHeight: 1.4,
                    }}
                  >
                    {translate("frozenInfo.description")}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      mt: 0.75,
                      lineHeight: 1.4,
                    }}
                  >
                    {translate("frozenInfo.description2")}
                  </Typography>
                </Box>
              }
              arrow
            >
              <AcUnit
                fontSize="small"
                sx={(theme) => ({
                  color: theme.palette.info.main,
                  opacity: 0.85,
                })}
              />
            </Tooltip>
          )}
          <Typography variant="body2" fontWeight={500}>
            {translate(label)}
          </Typography>

          {showNeutralInfo && (
            <Tooltip
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 260,
                    p: 1.5,
                  },
                },
              }}
              title={
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {translate("neutralInfo.title")}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.75,
                      lineHeight: 1.4,
                    }}
                  >
                    {translate("neutralInfo.description")}
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.5 }} aria-label="price-info">
                <InfoOutlined fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          {children}
        </Box>
      </Stack>
    );
  },
);
